<template>
  <div v-if="isOpen" class="copay-payment-overlay" @click.self="closeOverlay">
    <div class="copay-payment-modal">
      <div class="copay-payment-header">
        <h3>{{ $t('common.payment.title', 'Payment') }}</h3>
        <button class="close-btn" @click.stop="closeOverlay">
          <span>&times;</span>
        </button>
      </div>

      <div class="copay-payment-content">
        <div class="payment-info">
          <p class="copay-amount">{{ $t('plain.copay') }} <strong>${{ amount }}</strong></p>
          <p class="order-id">{{ $t('common.order_id', 'Order ID') }}: #{{ orderId }}</p>
        </div>

        <div class="payment-form">
          <div class="copay-message">
            <h4>{{ $t('common.payment.copay_payment', 'Copay Payment') }}</h4>
            <p>{{ $t('common.payment.copay_description', 'Complete your copay payment securely') }}</p>
          </div>

          <!-- Card Element как в driver-tips -->
          <div ref="cardElement" class="stripe-card-element">
            <!-- Stripe Card Element будет здесь -->
          </div>
          <div v-if="cardError" class="stripe-error" role="alert">{{ cardError }}</div>

          <!-- Payment Buttons как в driver-tips -->
          <div class="payment-buttons-block">
            <div class="payment-buttons">
              <!-- Pay Card Button -->
              <button
                type="button"
                class="btn btn--primary pay-card-btn"
                :disabled="!isStripeReady || isProcessing"
                @click="processStripePayment"
              >
                <span v-if="isProcessing" class="spinner"></span>
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" v-if="!isProcessing" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M4.103 4.468h13.75c.16 0 .321.01.48.03a2.834 2.834 0 00-3.33-2.387L3.695 4.041h-.012c-.71.136-1.341.538-1.765 1.123a3.763 3.763 0 012.185-.696zM17.854 5.5H4.104a2.753 2.753 0 00-2.75 2.75v8.25a2.753 2.753 0 002.75 2.75h13.75a2.753 2.753 0 002.75-2.75V8.25a2.754 2.754 0 00-2.75-2.75zm-2.042 8.25a1.375 1.375 0 110-2.75 1.375 1.375 0 010 2.75z" fill="#fff"/><path d="M1.375 11.15V6.875c0-.932.516-2.493 2.305-2.831C5.2 3.76 6.704 3.76 6.704 3.76s.988.687.171.687c-.816 0-.795 1.053 0 1.053s0 1.01 0 1.01l-3.201 3.63-2.299 1.01z" fill="#fff"/></svg>
                {{ isProcessing ? $t('common.payment.processing', 'Processing...') : `Pay Copay $${amount}` }}
              </button>

              <!-- Express Checkout (Google Pay / Apple Pay) -->
              <div ref="expressCheckout" class="buttons_stripe_container"></div>
            </div>
          </div>

          <div class="payment-actions">
            <button class="btn btn--secondary" @click.stop="closeOverlay">
              {{ $t('common.cancel', 'Cancel') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { paymentIntent } from '@/services/paymentService'
import { dashUrl } from '@/config'

export default {
  name: 'payment-overlay',
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    amount: {
      type: [String, Number],
      required: true,
    },
    orderId: {
      type: [String, Number],
      required: true,
    },
    orderData: {
      type: Object,
      required: true,
    },
  },
  emits: ['close', 'payment-initiated'],
  data() {
    return {
      stripe: null,
      elements: null,
      cardElement: null,
      expressElements: null,
      expressCheckoutElement: null,
      clientSecret: null,
      isStripeReady: false,
      isProcessing: false,
      showPaymentRequest: true, // всегда показываем
      cardError: '', // текст ошибки карты
    }
  },
  watch: {
    async isOpen(newValue) {
      if (newValue) {
        await this.$nextTick()
        this.initializeStripe()
        this.createPaymentIntent()
      } else {
        this.cleanup()
      }
    },
  },
  mounted() {
    // Проверяем есть ли copay payment_intent в URL после редиректа от Google Pay
    this.checkCopayPaymentResult()
  },

  beforeUnmount() {
    this.cleanup()
  },
  methods: {
    closeOverlay() {
      this.$emit('close')
    },

    async initializeStripe() {
      try {
        // Инициализация Stripe (нужно добавить ключ из конфигурации)
        if (!window.Stripe) {
          console.error('Stripe.js not loaded')
          return
        }

        this.stripe = window.Stripe(
          this.orderData.facility.customize.strip_public_key,
          {
            locale: 'en',
            apiVersion: '2020-08-27',
          },
        )

        // Создаем Express Elements для Google Pay / Apple Pay
        this.expressElements = this.stripe.elements({
          mode: 'payment',
          amount: Math.round(parseFloat(this.amount) * 100),
          currency: 'usd',
          appearance: {
            variables: {
              borderRadius: '8px',
            },
          },
        })

        // Инициализация Express Checkout (Google Pay / Apple Pay)
        await this.initializeExpressCheckout()

        // Создаем обычные Elements для Card Element
        this.elements = this.stripe.elements()

        // Создание Card Element
        this.cardElement = this.elements.create('card', {
          style: {
            base: {
              fontSize: '16px',
              color: '#333',
              '::placeholder': {
                color: '#aab7c4',
              },
            },
            invalid: {
              color: '#fa755a',
              iconColor: '#fa755a',
            },
          },
        })

        this.cardElement.mount(this.$refs.cardElement)

        // Обработчик ошибок карты
        this.cardElement.on('change', ({ error }) => {
          if (error) {
            this.cardError = error.message
          } else {
            this.cardError = ''
          }
        })

        this.isStripeReady = true
      } catch (error) {
        console.error('Error initializing Stripe:', error)
      }
    },

    async createPaymentIntent() {
      try {
        // Создание Payment Intent через paymentService
        const request = {
          amount: Math.round(parseFloat(this.amount) * 100), // в центах
          currency: 'usd',
          order_id: this.orderId,
          description: `Copay payment for order #${this.orderId}`,
        }

        const { payload } = await paymentIntent(request)

        this.intent = payload
        this.clientSecret = payload.client_secret
      } catch (error) {
        console.error('Error creating payment intent:', error)
        this.showError('Failed to initialize payment. Please try again.')
      }
    },

    async initializeExpressCheckout() {
      try {
        console.log('Initializing Express Checkout...')

        // Настройки для Express Checkout как в driver-tips
        const expressCheckoutOptions = {
          wallets: {
            applePay: 'always',
            googlePay: 'always',
          },
          buttonType: {
            googlePay: 'pay',
          },
          buttonHeight: 50,
          paymentMethodOrder: ['applePay', 'googlePay', 'link'],
          layout: {
            maxColumns: 1,
          },
        }

        // Создаем Express Checkout Element
        this.expressCheckoutElement = this.expressElements.create('expressCheckout', expressCheckoutOptions)

        // Монтируем элемент как в driver-tips
        await this.$nextTick()
        console.log('Express checkout ref found:', !!this.$refs.expressCheckout)

        if (this.$refs.expressCheckout) {
          await this.expressCheckoutElement.mount(this.$refs.expressCheckout)
          console.log('Express Checkout mounted successfully')
        }

        // Настраиваем обработчик как в driver-tips
        this.makePaymentRequest()
      } catch (error) {
        console.error('Error initializing Express Checkout:', error)
      }
    },

    makePaymentRequest() {
      // Точно как в driver-tips.vue
      this.expressCheckoutElement.on('confirm', () => {
        this.stripe.confirmPayment({
          elements: this.expressElements,
          clientSecret: this.clientSecret,
          confirmParams: {
            return_url: `${window.location.href}${window.location.href.includes('?') ? '&' : '?'}copay=true`,
          },
        }).then(result => {
          if (result.error) {
            console.error('Express checkout error:', result.error)
            this.cardError = result.error.message
          } else {
            this.handlePaymentMethodResult(result.paymentIntent)
            this.closeOverlay()
          }
        })
      })
    },

    async processStripePayment() {
      if (!this.stripe || !this.cardElement || !this.clientSecret || this.isProcessing) {
        return
      }

      this.isProcessing = true

      try {
        const { error, paymentIntent } = await this.stripe.confirmCardPayment(
          this.clientSecret,
          {
            payment_method: {
              card: this.cardElement,
              billing_details: {
                // Можно добавить данные пользователя
                name: 'Customer',
              },
            },
          },
        )

        if (error) {
          this.showError(error.message)
        } else if (paymentIntent.status === 'succeeded') {
          this.handlePaymentMethodResult(paymentIntent)
          this.closeOverlay()
        }
      } catch (error) {
        console.error('Payment error:', error)
        this.showError('Payment failed. Please try again.')
      } finally {
        this.isProcessing = false
      }
    },

    handlePaymentMethodResult(paymentIntent) {
      console.log('Payment intent:', paymentIntent)

      // Otherwise send paymentMethod.id to your server (see Step 3)
      fetch(`${dashUrl}/payment/copay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // body: JSON.stringify({ payment_method_id: result.paymentMethod.id, amount: fee.total, order_id: "<?= $oid ?>"}) // TEMP NOT UPPER FEE
        body: JSON.stringify({
          payment_method_id: paymentIntent.id,
          amount: this.amount,
          order_id: this.orderData.uid,
        }),
      })
        .then(result => result.json())
        .then(this.handleServerResponse)
    },

    handleServerResponse(responseJson) {
      if (responseJson.error) {
        // An error happened when charging the card, show it in the payment form
        this.cardError = responseJson.error

        this.$swal.fire({
          title: this.$t('common.payment.error'),
          type: 'error',
          text: this.$t('common.payment.transaction-error', { message: responseJson.error }),
          confirmButtonText: this.$t('common.payment.okay'),
          confirmButtonClass: 'btn-danger',
        })
      } else {
        this.$swal.fire({
          title: this.$t('common.payment.success'),
          type: 'success',
          text: this.$t('common.payment.copay-success'),
          confirmButtonText: this.$t('common.payment.okay'),
          confirmButtonClass: 'btn-success',
        })
        // Show a success message
        setTimeout(() => {
          window.location.reload()
        }, 2000)
      }
    },

    showError(message) {
      this.cardError = message
    },

    checkCopayPaymentResult() {
      const urlParams = new URLSearchParams(window.location.search)
      const paymentIntent = urlParams.get('payment_intent')
      const clientSecret = urlParams.get('payment_intent_client_secret')
      const redirectStatus = urlParams.get('redirect_status')
      const isCopayPayment = urlParams.get('copay') === 'true'

      console.log('URL params:', {
        paymentIntent,
        clientSecret,
        redirectStatus,
        isCopayPayment,
      })

      if (paymentIntent && isCopayPayment && redirectStatus === 'succeeded') {
        console.log('Copay payment completed via Google Pay:', {
          paymentIntent,
          clientSecret,
          redirectStatus,
        })

        // Создаем объект paymentIntent для обработки с полными данными
        const paymentIntentObj = {
          id: paymentIntent,
          client_secret: clientSecret,
          status: 'succeeded',
        }

        // Обрабатываем так же как и обычные платежи
        this.handlePaymentMethodResult(paymentIntentObj)

        // Очищаем URL параметры
        const url = new URL(window.location)
        url.searchParams.delete('payment_intent')
        url.searchParams.delete('payment_intent_client_secret')
        url.searchParams.delete('redirect_status')
        url.searchParams.delete('copay')
        window.history.replaceState({}, '', url.toString())
      }
    },

    cleanup() {
      if (this.cardElement) {
        this.cardElement.destroy()
        this.cardElement = null
      }
      if (this.expressCheckoutElement) {
        this.expressCheckoutElement.destroy()
        this.expressCheckoutElement = null
      }
      this.expressElements = null
      this.isStripeReady = false
      this.clientSecret = null
      this.isProcessing = false
      this.cardError = '' // очищаем ошибки при закрытии
    },
  },
}
</script>

<style lang="scss" scoped>
.copay-payment-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.copay-payment-modal {
  background: white;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.copay-payment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #eee;

  h3 {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: #333;
  }
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
    color: #333;
  }
}

.copay-payment-content {
  padding: 24px;
}

.payment-info {
  margin-bottom: 24px;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #007bff;
}

.copay-amount {
  font-size: 18px;
  margin: 0 0 8px 0;
  color: #333;
}

.order-id {
  font-size: 14px;
  margin: 0;
  color: #666;
}

.copay-message {
  margin-bottom: 24px;
  text-align: center;

  h4 {
    margin: 0 0 8px 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
  }

  p {
    margin: 0;
    color: #666;
    font-size: 14px;
  }
}

.payment-buttons-block {
  margin-bottom: 24px;
}

.payment-buttons {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.pay-card-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.buttons_stripe_container {
  // Стили для Express Checkout контейнера
  .StripeElement {
    border-radius: 8px;
  }
}

.stripe-card-element {
  padding: 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  background-color: white;
  transition: border-color 0.2s ease;
  margin-bottom: 24px;

  &:focus-within {
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
}

.stripe-error {
  color: #fa755a;
  font-size: 14px;
  margin-bottom: 24px;
}

.payment-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 0;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;

  &--secondary {
    background-color: #6c757d;
    color: white;

    &:hover {
      background-color: #5a6268;
    }
  }

  &--primary {
    background-color: #007bff;
    color: white;
    position: relative;

    &:hover:not(:disabled) {
      background-color: #0056b3;
    }

    &:disabled {
      background-color: #ccc;
      cursor: not-allowed;
    }
  }
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// Responsive
@media (max-width: 480px) {
  .copay-payment-modal {
    margin: 20px;
    width: calc(100% - 40px);
  }

  .payment-actions {
    flex-direction: column;

    .btn {
      width: 100%;
    }
  }
}
</style>
